import dayjs, { Dayjs } from 'dayjs';
import { uid } from 'uid';
import { OfficialHoliday } from '../officialHoliday/OfficialHoliday.types';
import { DayOfWeek } from '../time/Timetracking.types';
import { SchambeckUser, TimeConfirmation, WorkSchedule } from '../user/User.types';

/**
 * Helper to get dates with the same time
 * @param date to delete time from
 * @returns date with standard hours and no other times
 */
export const getDateWithoutTime = (date?: Date): Date => {
  const newDate: Date = date || new Date();
  const offset: number = newDate.getTimezoneOffset();
  newDate.setHours(12 - offset / 60);
  newDate.setMinutes(0);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);
  return newDate;
};

/**
 * Helper to get difference in Days
 * @param startDate to check
 * @param endDate to check difference to startDate
 * @returns number of days difference
 */
export const getDifferenceInDays = (startDate: Date, endDate: Date): number => {
  const dateOne = dayjs(startDate);
  const dateTwo = dayjs(endDate);
  return dateTwo.diff(dateOne, 'days');
};

/**
 * Util method to calculate VacationDays needed in a range between to dates
 * @param startDate of the range to calculate
 * @param endDate  of the range to calculate
 * @param userWorkSchedule to calculate the vacationdays for
 * @param officialHolidaysInRange List of all officialHolidays in the dateRange
 * @returns number of vacationDays that the user needs to make the vacation
 * @tested
 */
export const calculateVacationDaysForDateRange = (
  startDate: Date,
  endDate: Date,
  userWorkSchedule: WorkSchedule[],
  officialHolidaysInRange: Dayjs[],
  partial?: boolean,
): number | undefined => {
  if (userWorkSchedule.length === 0) return undefined;

  let vacationDays: number = 0;

  let currDate: Dayjs = dayjs(startDate);
  while (currDate.isBefore(endDate, 'day') || currDate.isSame(endDate, 'day')) {
    if (
      !officialHolidaysInRange.some((day) => day.isSame(currDate, 'day')) &&
      userWorkSchedule.some(
        (schedule) => schedule.dayOfWeek === convertDateToDayOfWeek(currDate.day()),
      )
    ) {
      const foundSchedule: WorkSchedule = userWorkSchedule.find(
        (schedule) => schedule.dayOfWeek === convertDateToDayOfWeek(currDate.day()),
      )!;
      if (foundSchedule.workingHours > 0) vacationDays += 1;
    }
    currDate = currDate.add(1, 'day');
  }

  return vacationDays === 1 && partial ? 0.5 : vacationDays;
};

/**
 * Helper to convert dayJs.day() to dayOfWeek
 * @param day number of the day
 * @returns correct dayOfWeek to the number of day
 */
export const convertDateToDayOfWeek = (day: number): DayOfWeek | undefined => {
  switch (day) {
    case 0:
      return DayOfWeek.SUNDAY;
    case 1:
      return DayOfWeek.MONDAY;
    case 2:
      return DayOfWeek.TUESDAY;
    case 3:
      return DayOfWeek.WEDNESDAY;
    case 4:
      return DayOfWeek.THURSDAY;
    case 5:
      return DayOfWeek.FRIDAY;
    case 6:
      return DayOfWeek.SATURDAY;
    default:
      return;
  }
};

/**
 * Util method to get the scheduled workTime for a given range
 * @param schedules the user schedules
 * @param startDate start of the range
 * @param endDate  end of the range
 * @param officialHolidays officialHolidays to check
 * @returns amount of workingHours inside the range
 * @tested
 */
export const getScheduledWorkTimeForRange = (
  schedules: WorkSchedule[],
  startDate: Date,
  endDate: Date,
  officialHolidays: OfficialHoliday[],
): number => {
  const scheduledTimes: Map<DayOfWeek, number> = new Map<DayOfWeek, number>();
  schedules.forEach((schedule) => scheduledTimes.set(schedule.dayOfWeek, schedule.workingHours));
  let scheduledTime: number = 0;

  for (
    let curDate: dayjs.Dayjs = dayjs(startDate);
    curDate.isBefore(endDate) || curDate.isSame(endDate);
    curDate = curDate.add(1, 'day')
  ) {
    const isPublicHoliday: boolean = officialHolidays.some((holiday) =>
      curDate.isSame(holiday.date),
    );
    if (isPublicHoliday) continue;
    const dayOfWeek: DayOfWeek | undefined = convertDateToDayOfWeek(curDate.get('day'));
    if (!dayOfWeek) continue;
    const workTime: number = scheduledTimes.get(dayOfWeek) || 0;
    scheduledTime += workTime;
  }
  return scheduledTime;
};

/**
 * Util method to generate an empty timeConfirmation
 * @param confirmedBy reuired information on who created the confirmation
 * @param override partial confirmation to adjust created instance
 * @returns generated timeConfirmation
 */
export const generateEmptyTimeConfirmation = (
  confirmedBy: string,
  override?: Partial<TimeConfirmation>,
): TimeConfirmation => ({
  comment: '',
  confirmationDate: new Date(),
  confirmedBy,
  month: new Date().getMonth() + 1,
  invalid: false,
  id: uid(),
  ...override,
});

/**
 * Util method to check if a specific month has a valid confirmation
 * @param month the month to check, based on 1-12
 * @param year the full year
 * @param user the user to check
 * @returns true if a valid confirmation was found or false if not
 */
export const checkForValidConfirmation = (
  month: number,
  year: number,
  user: SchambeckUser,
): boolean => {
  return (
    !!user.contractInformation.yearlyTimes &&
    !!user.contractInformation.yearlyTimes
      .get(year)
      ?.confirmations.some((confirmation) => !confirmation.invalid && confirmation.month === month)
  );
};
